import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { useAllUsers } from 'context/AllUserDataContext';
import useConfirmationDialog from 'routes/Pages/components/ConfirmDialogs/useConfirmationDialog';
import useUrlParam from 'services/globalHooks/useUrlParam';

const useGenTaskController = () => {
  const [genTaskData, setGenTaskData] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const { showToast } = useToast();
  const {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,
  } = useConfirmationDialog();
  const { getAllUsers } = useAllUsers();

  // ── Edit dialog ──────────────────────────────────────────────────────────────
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedGenTask, setSelectedGenTask] = useState(null);

  // ── Add dialog ───────────────────────────────────────────────────────────────
  const [openAddDialog, setOpenAddDialog] = useState(false);

  // ── Dropdown options ─────────────────────────────────────────────────────────
  const [vehicalList, setVehicalList] = useState([]);
  const [trailerList, setTrailerList] = useState([]);
  const [checkListOption, setChecklistOptions] = useState([]);
  const [typeOfRepairoption, setTypeOfRepairOptions] = useState([]);

  const { getParam, setParam, deleteParam } = useUrlParam();

  useEffect(() => {
    isMounted.current = true;
    getGenTaskData();
    getAllUsers();
    getDropdownOptions();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const taskIdFromUrl = getParam('taskID');
    if (taskIdFromUrl && !openEditDialog && genTaskData.length > 0) {
      const taskToOpen = genTaskData.find(task => String(task.id) === String(taskIdFromUrl));
      if (taskToOpen) {
        // Use a functional wrapper to make sure state setter picks up latest data
        setOpenEditDialog(true);
        setSelectedGenTask(taskToOpen);
      }
    }
  }, [genTaskData, getParam, openEditDialog]);

  const getDropdownOptions = async () => {
    const dropdownKeys = ['Checklist', 'Trailer', 'Truck', 'TypeOfRepair'];
    const allPromise = dropdownKeys.map(item =>
      customAxios.get(`/GenTasks/GetDropdownList/${item}`, { headers: authHeader() }),
    );

    await Promise.all(allPromise)
      .then(([checklistres, trailerRes, truckRes, typeOfrepairRes]) => {
        if (checklistres.status === 200 && checklistres.data?.length > 0)
          setDropDownOption(checklistres.data, 'keyValue', setChecklistOptions);
        if (trailerRes.status === 200 && trailerRes.data?.length > 0)
          setDropDownOption(trailerRes.data, 'keyValue', setTrailerList);
        if (truckRes.status === 200 && truckRes.data?.length > 0)
          setDropDownOption(truckRes.data, 'keyValue', setVehicalList);
        if (typeOfrepairRes.status === 200 && typeOfrepairRes.data?.length > 0)
          setDropDownOption(typeOfrepairRes.data, 'keyValue', setTypeOfRepairOptions);
      })
      .catch(error => {
        console.error('Dropdown fetch failed:', error);
      });
  };

  const setDropDownOption = (dpData, keyName, setFunc) => {
    setFunc(dpData.map(item => item[keyName]));
  };

  const getGenTaskData = () => {
    dispatch({ type: FETCH_START });
    customAxios
      .get('/GenTasks/GetTasks', { headers: authHeader() })
      .then(data => {
        if (data.status === 200) {
          if (isMounted.current) setGenTaskData(data.data);
        } else {
          showToast('Failed to load General Task data', 'error');
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(() => {
        showToast('Failed to load General Task data', 'error');
      });
  };

  const handleTabChange = (event, val) => setCurrentTab(val);

  // ── Edit handlers ────────────────────────────────────────────────────────────
  const handleOpeneditDialogClick = taskData => {
    setSelectedGenTask(taskData);
    setOpenEditDialog(true);
    setParam('taskID', taskData?.id);
  };

  const handleCloseEditDialogClick = () => {
    setSelectedGenTask(null);
    setOpenEditDialog(false);
    deleteParam('taskID');
  };

  const handleSuccessfulSubmit = () => {
    handleCloseEditDialogClick();
    getGenTaskData();
    showToast('Successfully Updated', 'success');
  };

  // ── Add handlers ─────────────────────────────────────────────────────────────
  const handleSuccessfulAdd = () => {
    handleCloseEditDialogClick();
    getGenTaskData();
    showToast('Task Added Successfully', 'success');
  };

  return {
    // confirmation
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,
    // data
    genTaskData,
    setGenTaskData,
    // tabs
    handleTabChange,
    currentTab,
    // edit
    handleOpeneditDialogClick,
    selectedGenTask,
    openEditDialog,
    setOpenEditDialog,
    handleSuccessfulSubmit,
    // add
    openAddDialog,
    setOpenAddDialog,
    handleSuccessfulAdd,
    // dropdowns
    vehicalList,
    trailerList,
    checkListOption,
    typeOfRepairoption,

    handleCloseEditDialogClick,
  };
};

export default useGenTaskController;
